import {
  CarrierAccountDetails,
  ConfirmOrderRequest,
  ShipmentTransportationDetails,
  ShippingPartner,
} from "@deliverr/replenishment-client";
import { FormattedMessage } from "common/components/ui/FormattedMessage";
import { SPThunkAction } from "common/ReduxUtils";
import { getSellerId } from "common/user/UserSelectors";
import { logError } from "Logger";
import React from "react";
import { toast } from "react-toastify";
import { selectTransferCreate } from "transfers/create/store/TransferCreateSelectors";
import { getReplenishmentShippingTypeFromWholesaleType } from "../utils/getReplenishmentShippingTypeFromWholesaleType";
import { getReplenishmentShippingPartnerFromDistributionChannel } from "../utils/getReplenishmentShippingPartnerFromDistributionChannel";
import { replenishmentClient } from "Clients";
import { includes, isEmpty, map } from "lodash";
import { transferCreateSetReplenishmentError } from "transfers/create/actions";
import { throwIfEmpty } from "common/utils/ValidationUtils";
import { ReplenishmentConstants } from "transfers/constants/ReplenishmentConstants";

export const confirmDraftReplenishmentOrder = (): SPThunkAction<Promise<boolean>> => async (dispatch, getState) => {
  const state = getState();
  const sellerId = getSellerId(state);

  const {
    replenishmentOrder,
    cargoType,
    shippingMethod,
    distributionChannel,
    shipmentShippingMethodMap,
    selectedCarrierAccount,
  } = selectTransferCreate(state);

  const ctx = {
    fn: "transfers.steps.common.confirmDraftReplenishmentOrder",
    distributionChannel,
    sellerId,
    orderId: replenishmentOrder?.orderId,
    shippingMethod,
    cargoType,
  };

  try {
    const orderId = replenishmentOrder?.orderId;

    throwIfEmpty(orderId, "orderId");

    const shipments = replenishmentOrder?.shipments;

    throwIfEmpty(shipments, "shipments");

    throwIfEmpty(distributionChannel, "distributionChannel");

    const shipmentTransportationDetails: ShipmentTransportationDetails[] = map(shipments, ({ shipmentId }) => {
      const shippingType = getReplenishmentShippingTypeFromWholesaleType(
        shipmentShippingMethodMap?.[shippingMethod!]?.[shipmentId] ?? cargoType
      );
      const shippingPartner = getReplenishmentShippingPartnerFromDistributionChannel(
        shippingMethod!,
        distributionChannel
      );

      const carrierAccountDetails: CarrierAccountDetails | undefined =
        selectedCarrierAccount && shippingPartner === ShippingPartner.COLLECT
          ? {
              carrierName: selectedCarrierAccount.carrier,
              accountNumber: selectedCarrierAccount.accountNumber,
              country: selectedCarrierAccount.country,
              postalCode: selectedCarrierAccount.postalCode,
              paymentType: selectedCarrierAccount.paymentType,
            }
          : undefined;

      return {
        shipmentId,
        transportationDetails: {
          shippingPartner,
          shippingType,
          carrierAccountDetails,
        },
      };
    });

    const createInChannel = includes(ReplenishmentConstants.ECOM_CHANNELS, distributionChannel);

    const confirmOrderRequest: ConfirmOrderRequest = {
      shipmentTransportationDetails,
      validateInventory: true,
      validateAddress: false,
      createInChannel,
    };

    await replenishmentClient.confirmOrder(orderId, confirmOrderRequest);
    return true;
  } catch (error) {
    const errorData = error.response?.data?.error;

    dispatch(transferCreateSetReplenishmentError({ errors: !isEmpty(errorData) ? [errorData] : undefined }));

    logError(ctx, error, "Error confirming draft replenishment order");

    toast.error(
      <FormattedMessage
        id="transfers.create.actions.replenishmentOrder.confirmDraftReplenishmentOrderError"
        defaultMessage="Failed to confirm the order. Please try again."
      />
    );
    return false;
  }
};
